import type { ReactNode } from 'react'

import type { Branch, CustomerWriteRequest } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { branchDescription } from '@/lib/branch'
import { formatShortDate } from '@/lib/datetime'

import { ADDRESS_TYPE_LABELS, CONSENT_STATEMENTS } from '../constants'

/**
 * The review step.
 *
 * It renders the request object itself rather than the form state, so what an operator confirms
 * is literally what leaves the browser. A second rendering of the same values, assembled
 * independently, is a second chance to be wrong about them.
 */
export interface ReviewStepProps {
  request: CustomerWriteRequest
  branches: Branch[]
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-heading">
        {empty ? <span className="text-muted">Not recorded</span> : value}
      </dd>
    </div>
  )
}

export function ReviewStep({ request, branches }: ReviewStepProps) {
  const branch = branches.find((candidate) => candidate.id === request.branchId)
  const branchName = branch ? branchDescription(branch) : `Branch ${request.branchId}`

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-body">
        This is exactly what will be sent. The record is created in draft — it becomes a customer
        who can hold accounts only after a supervisor approves the KYC file, and that supervisor
        cannot be whoever submits it.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Identity</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Row
                label="Name"
                value={[request.firstName, request.middleName, request.lastName]
                  .filter(Boolean)
                  .join(' ')}
              />
              <Row label="Home branch" value={branchName} />
              <Row label="Gender" value={request.gender} />
              <Row label="Date of birth" value={formatShortDate(request.dateOfBirth)} />
              <Row label="Place of birth" value={request.placeOfBirth} />
              <Row label="Nationality" value={request.nationality} />
              <Row label="Marital status" value={request.maritalStatus} />
              <Row
                label="Ghana Card"
                value={<span className="font-mono">{request.ghanaCardNumber}</span>}
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact and livelihood</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Row label="Mobile" value={<span className="font-mono">{request.phoneNumber}</span>} />
              <Row
                label="Alternate mobile"
                value={
                  request.altPhoneNumber ? (
                    <span className="font-mono">{request.altPhoneNumber}</span>
                  ) : null
                }
              />
              <Row label="Email" value={request.email} />
              <Row label="Occupation" value={request.occupation} />
              <Row label="Employer or business" value={request.employerOrBusinessName} />
              <Row
                label="Monthly income estimate"
                value={
                  <MoneyDisplay value={request.monthlyIncomeEstimateMinor} className="text-left" />
                }
              />
            </dl>
          </CardContent>
        </Card>

        {request.addresses.map((address) => (
          <Card key={address.addressType}>
            <CardHeader>
              <CardTitle>{ADDRESS_TYPE_LABELS[address.addressType]} address</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Row
                  label="GhanaPost GPS"
                  value={<span className="font-mono">{address.ghanaPostGps}</span>}
                />
                <Row label="Street or area" value={address.streetOrArea} />
                <Row label="Landmark" value={address.landmark} />
                <Row label="Town" value={address.town} />
                <Row label="District" value={address.district} />
                <Row label="Region" value={address.region} />
              </dl>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardTitle>Identity documents</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              {request.identityDocuments.map((document) => (
                <Row
                  key={document.idNumber}
                  label={document.idType === 'GhanaCard' ? 'Ghana Card' : document.idType}
                  value={
                    <span className="font-mono">
                      {document.idNumber}
                      {document.expiryDate ? ` · expires ${formatShortDate(document.expiryDate)}` : ''}
                    </span>
                  }
                />
              ))}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Next of kin</CardTitle>
          </CardHeader>
          <CardContent>
            {request.nextOfKin.length === 0 ? (
              <p className="text-sm text-muted">None captured.</p>
            ) : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                {request.nextOfKin.map((kin) => (
                  <Row
                    key={`${kin.fullName}-${kin.phoneNumber}`}
                    label={kin.relationship}
                    value={
                      <>
                        {kin.fullName}
                        <span className="block font-mono text-xs text-body">{kin.phoneNumber}</span>
                      </>
                    }
                  />
                ))}
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Consents</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {request.consents.map((consent) => {
                const statement = CONSENT_STATEMENTS.find(
                  (candidate) => candidate.consentType === consent.consentType,
                )

                return (
                  <li
                    key={consent.consentType}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="text-body">{statement?.title ?? consent.consentType}</span>
                    <Badge tone={consent.granted ? 'success' : 'neutral'}>
                      {consent.granted ? 'Granted' : 'Withheld'}
                    </Badge>
                  </li>
                )
              })}
            </ul>
            <p className="mt-3 text-2xs text-muted">
              Stored against text version {request.consents[0]?.textVersion ?? '—'}.
            </p>
          </CardContent>
        </Card>
      </div>

      <details className="rounded-card border border-border-subtle bg-surface p-3">
        <summary className="cursor-pointer text-xs font-medium text-brand-text">
          The payload, exactly as it will be sent
        </summary>
        <pre className="mt-2 max-h-80 overflow-auto rounded-control border border-border-subtle bg-surface-muted p-2 font-mono text-2xs text-body">
          {JSON.stringify(request, null, 2)}
        </pre>
      </details>
    </div>
  )
}
